import React from "react";
import theme from "@config/theme";
import Breadcrumbs from "./Breadcrumbs";

type Props = {
  children: React.ReactNode;
  galaxyTitle: string;
  galaxyURL: string;
  pageTitle: string;
  pageURL: string;
};

const getLinks = (
  galaxyTitle: string,
  galaxyURL: string,
  pageTitle: string,
  pageURL: string,
) => {
  const links = [
    { name: "Docs", url: "/docs" },
    { name: galaxyTitle, url: galaxyURL },
  ];

  const urlWithoutGalaxy = pageURL.replace(`${galaxyURL}/`, "");
  if (urlWithoutGalaxy.includes("/")) {
    const pageFolder = urlWithoutGalaxy.split("/")[0];
    const pageFolderWords = pageFolder.split("-");
    let pageFolderTitle = pageFolderWords
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    pageFolderTitle = pageFolderTitle.replace("Api", "API");
    links.push({ name: pageFolderTitle, url: `${galaxyURL}/${pageFolder}` });
  }

  links.push({ name: pageTitle, url: pageURL });

  return links;
};

const Doc = ({
  children,
  galaxyTitle,
  galaxyURL,
  pageTitle,
  pageURL,
}: Props) => (
  <div className="doc">
    <Breadcrumbs links={getLinks(galaxyTitle, galaxyURL, pageTitle, pageURL)} />
    {children}
    <style jsx global>
      {`
        .doc {
          padding: 0 0 3.5rem 0;
        }

        .doc section {
          display: inline-block;
          max-width: 100%;
        }

        .doc .doc-image img {
          border-radius: 4px;
        }

        .doc .doc-image {
          padding: 1rem 0 1.5rem 0;
          break-inside: avoid; // keeps printed version of page from breaking "boxes" of text across multiple pages - mdn link: https://developer.mozilla.org/en-US/docs/Web/CSS/break-inside
          display: block;
        }

        .doc table .doc-image {
          padding: 0;
        }

        .doc h1 {
          margin-top: 0;
        }

        h2,
        h3,
        h4,
        h5,
        h6 {
          margin-top: 2.5rem;
        }

        .doc h1 + p {
          font-size: 1rem;
        }

        .doc details {
          margin-bottom: 1.5rem;
          border: 1px solid ${theme.colors.turquoise};
          border-radius: 4px;
          padding: 0.75rem 1rem;
        }

        .doc details[open] summary {
          margin-top: 0.5rem;
          margin-bottom: 0.5rem;
        }

        .doc details summary {
          font-size: ${theme.fonts.size.px18};
          font-weight: 700;
          color: #3b8b9a;
          cursor: pointer;
          outline: 0;
          user-select: none;
        }

        .pinout table {
          border: none;
        }

        .pinout thead th {
          color: ${theme.colors.white};
          background-color: ${theme.colors.notehubBlueTint};
          border-left: none;
          border-right: none;
          border-bottom: none;
        }

        .pinout thead {
          border-bottom: none;
        }

        .pinout table tr:first-child td:first-child,
        .pinout table tr:first-child td:last-child {
          border-top: none !important;
        }

        .pinout table tr:last-child td:nth-child(2),
        .pinout table tr:last-child td:nth-child(3),
        .pinout table tr:last-child td:nth-child(4),
        .pinout table tr:last-child td:nth-child(5) {
          border-bottom: 2px solid ${theme.colors.notehubBlueTint};
        }

        .pinout table td:nth-child(2) {
          border-left: 2px solid ${theme.colors.notehubBlueTint};
        }

        .pinout table td:nth-child(6) {
          border-left: 2px solid ${theme.colors.notehubBlueTint};
        }

        .pinout table td:nth-child(2),
        .pinout table td:nth-child(3),
        .pinout table td:nth-child(4),
        .pinout table td:nth-child(5) {
          background-color: ${theme.colors.lightSnowBlue};
          border-top-color: ${theme.colors.lightSnowBlue};
        }

        .pinout table td:nth-child(3),
        .pinout table td:nth-child(4),
        .pinout table td:nth-child(5) {
          border-right: none;
          border-left: none;
        }

        .pinout table tbody tr:hover {
          background: none;
        }
      `}
    </style>
  </div>
);

export default Doc;
